import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Store, MapPin, Phone, Mail, Wrench, Search, ChevronRight,
  Clock, CheckCircle2, AlertCircle, Package
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  'en_attente': { labelKey: 'publicShop.status.en_attente', color: '#fedd00', icon: Clock },
  'en_cours': { labelKey: 'publicShop.status.en_cours', color: '#3b82f6', icon: Wrench },
  'termine': { labelKey: 'publicShop.status.termine', color: '#009b3a', icon: CheckCircle2 },
  'pret': { labelKey: 'publicShop.status.pret', color: '#009b3a', icon: Package },
  'livre': { labelKey: 'publicShop.status.livre', color: '#10b981', icon: CheckCircle2 },
  'annule': { labelKey: 'publicShop.status.annule', color: '#ef4444', icon: AlertCircle },
};

const PublicShopPage = () => {
  const { t } = useLanguage();
  const { shopId } = useParams();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingCode, setTrackingCode] = useState('');
  const [trackResult, setTrackResult] = useState(null);
  const [trackError, setTrackError] = useState('');
  const [trackLoading, setTrackLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/shops/${shopId}`)
      .then(r => setShop(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [shopId]);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingCode.trim()) return;
    setTrackLoading(true);
    setTrackError('');
    setTrackResult(null);
    try {
      const res = await axios.get(`${API}/api/public/shops/${shopId}/track/${trackingCode.trim()}`);
      setTrackResult(res.data);
    } catch (err) {
      setTrackError(err.response?.data?.detail || t('publicShop.repairNotFound'));
    } finally { setTrackLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#050a14] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" /></div>;
  if (!shop) return <div className="min-h-screen bg-[#050a14] flex items-center justify-center text-white"><p>{t('publicShop.shopNotFound')}</p></div>;

  return (
    <div className="min-h-screen bg-[#050a14] text-white" data-testid="public-shop-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#009b3a]/20 to-[#fedd00]/10 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#009b3a]/15 flex items-center justify-center">
              <Store className="w-8 h-8 text-[#009b3a]" />
            </div>
            <div>
              <h1 className="text-2xl font-black">{shop.name}</h1>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-400 flex-wrap">
                {shop.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{shop.city}</span>}
                {shop.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{shop.phone}</span>}
                {shop.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{shop.email}</span>}
              </div>
            </div>
          </div>
          {shop.address && <p className="mt-3 text-sm text-gray-500">{shop.address}</p>}
          {shop.description && <p className="mt-2 text-sm text-gray-400">{shop.description}</p>}
        </div>
      </div>

      {/* Tracking */}
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="bg-[#111827] border border-white/5 rounded-2xl p-6">
          <h2 className="text-base font-bold text-white mb-1">{t('publicShop.trackTitle')}</h2>
          <p className="text-xs text-gray-500 mb-4">{t('publicShop.trackSubtitle')}</p>
          <form onSubmit={handleTrack} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text" value={trackingCode} onChange={e => setTrackingCode(e.target.value)}
                placeholder={t('publicShop.trackPlaceholder')}
                data-testid="tracking-input"
                className="w-full pl-10 pr-4 py-3 bg-[#0a0f1c] border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#009b3a]/40"
              />
            </div>
            <button type="submit" disabled={trackLoading} data-testid="tracking-submit" className="px-6 py-3 rounded-xl text-sm font-semibold text-[#050a14] bg-[#009b3a] hover:bg-[#00b344] disabled:opacity-50 transition-all">
              {trackLoading ? '...' : t('publicShop.trackButton')}
            </button>
          </form>

          {trackError && (
            <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400" data-testid="tracking-error">
              {trackError}
            </div>
          )}

          {trackResult && (
            <div className="mt-6 bg-[#0a0f1c] rounded-xl p-5 border border-white/5" data-testid="tracking-result">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-white">{trackResult.marque} {trackResult.modele}</h3>
                {(() => {
                  const s = STATUS_MAP[trackResult.statut];
                  const Icon = s ? s.icon : Clock;
                  const label = s ? t(s.labelKey) : trackResult.statut;
                  const color = s ? s.color : '#999';
                  return (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold" style={{background: `${color}20`, color}}>
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </span>
                  );
                })()}
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {trackResult.date_entree && <div><span className="text-gray-500">{t('publicShop.labelDateEntree')}</span><p className="text-white mt-0.5">{trackResult.date_entree}</p></div>}
                {trackResult.date_sortie_prevue && <div><span className="text-gray-500">{t('publicShop.labelSortiePrevue')}</span><p className="text-white mt-0.5">{trackResult.date_sortie_prevue}</p></div>}
                {trackResult.probleme && <div className="col-span-2"><span className="text-gray-500">{t('publicShop.labelProbleme')}</span><p className="text-gray-300 mt-0.5">{trackResult.probleme}</p></div>}
                {trackResult.notes_client && <div className="col-span-2"><span className="text-gray-500">{t('publicShop.labelRemarques')}</span><p className="text-gray-300 mt-0.5">{trackResult.notes_client}</p></div>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6 px-4 text-center">
        <a href="/" className="text-xs text-gray-600 hover:text-[#009b3a] transition-colors">
          {t('publicShop.poweredBy', { brand: 'GUYAREPAIR' })}
        </a>
      </footer>
    </div>
  );
};

export default PublicShopPage;
